/**
 * **The Odżywianie chart** — one column per day (or per week on 12 weeks), the target as a dashed
 * STEP where it changed (R3), and the client's weight on its own right-hand axis when asked for.
 *
 * Hand-drawn SVG rather than `shared/ui/chart/ReadoutChart`, and deliberately: that primitive is a
 * single time series on a time axis, and this plot is a row of categorical columns whose every bar
 * carries its own verdict ink, a sugar veil inside the carbohydrate bar, a per-column target step and
 * a second y axis. Nothing here is decided — every figure, ink and word is `model/nutritionStage`'s.
 *
 * The readout keeps the client-detail rule: the pointer anywhere over the plot picks the nearest
 * column, and the plot is a tab stop whose arrows walk the columns with the same panel. As in
 * `ReadoutChart`, the tab stop itself is the `role="img"`, a keyboard step is announced through a
 * polite status beside it, and a visually hidden table carries every column, beside the image
 * rather than in it.
 */

import { useRef, useState } from 'react';

import { OWNS_ARROWS_ATTRIBUTE } from '~/shared/lib/ownsArrows';
import { useLaidOutWidth } from '~/shared/lib/useLaidOutWidth';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { NutritionChartColumn, NutritionChartView } from '../model/nutritionStage';

import styles from './NutritionTab.module.css';

/** The plot box, in CSS pixels: the mockup's 250px stage with its 44px left gutter. */
const HEIGHT = 250;
const LEFT = 44;
const TOP = 22;
const BOTTOM = 26;
/** The right gutter holds the target chip, and the weight axis when the line is on. */
const RIGHT_PLAIN = 64;
const RIGHT_WEIGHT = 104;
/** The target chip keeps the plain gutter's width, so the weight axis has its own column beside it. */
const CHIP_WIDTH = RIGHT_PLAIN - 12;
/** The narrowest the plot is drawn at; a narrower card scrolls it sideways. */
const MIN_WIDTH = 560;
const BAR_SHARE = 0.56;
const BAR_MAX = 34;

const BAR_CLASS: Readonly<Record<NutritionChartColumn['kind'], string | undefined>> = {
  ok: styles.barOn,
  warn: styles.barOff,
  plain: styles.barPlain,
  today: styles.barCurrent,
  none: undefined,
  na: undefined,
};

interface NutritionChartProps {
  readonly chart: NutritionChartView;
  /** The headings of the hidden table's columns. */
  readonly tableHeads: { readonly when: string; readonly reading: string };
  readonly legend: readonly { readonly key: string; readonly label: string }[];
  readonly className?: string;
}

export function NutritionChart({ chart, tableHeads, legend, className }: NutritionChartProps) {
  const box = useRef<HTMLDivElement | null>(null);
  const measured = useLaidOutWidth(box, true);
  const [active, setActive] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const width = Math.max(measured ?? MIN_WIDTH, MIN_WIDTH);
  const right = chart.weight === null ? RIGHT_PLAIN : RIGHT_WEIGHT;
  const count = Math.max(1, chart.columns.length);
  const columnWidth = (width - LEFT - right) / count;
  const barWidth = Math.min(BAR_MAX, columnWidth * BAR_SHARE);
  const base = HEIGHT - BOTTOM;
  const y = (value: number) => TOP + (1 - value / chart.yMax) * (base - TOP);
  const centre = (index: number) => LEFT + columnWidth * index + columnWidth / 2;
  const weight = chart.weight;
  const weightY = (kg: number) =>
    weight === null ? 0 : TOP + (1 - (kg - weight.lo) / (weight.hi - weight.lo)) * (base - TOP);

  const pick = (clientX: number, target: SVGSVGElement) => {
    const x = clientX - target.getBoundingClientRect().left;
    const index = Math.floor((x - LEFT) / columnWidth);
    setActive(index >= 0 && index < chart.columns.length ? index : null);
  };
  const shown = active === null ? null : (chart.columns[active] ?? null);
  const step = (index: number) => {
    const column = chart.columns[index];
    if (column === undefined) return;
    setActive(index);
    setAnnouncement(`${column.readout.title}: ${column.readout.lines.join(', ')}`);
  };

  return (
    <div className={`${String(styles.chartBlock)} ${className ?? ''}`}>
      <div
        ref={box}
        className={styles.chartBox}
        role="img"
        tabIndex={0}
        aria-label={chart.name}
        {...{ [OWNS_ARROWS_ATTRIBUTE]: '' }}
        onFocus={() => {
          if (active === null) step(chart.columns.length - 1);
        }}
        onKeyDown={(event) => {
          const last = chart.columns.length - 1;
          const moves: Record<string, number | undefined> = {
            ArrowLeft: Math.max(0, (active ?? last + 1) - 1),
            ArrowRight: Math.min(last, (active ?? -1) + 1),
            Home: 0,
            End: last,
          };
          const next = moves[event.key];
          if (next !== undefined) {
            event.preventDefault();
            step(next);
          } else if (event.key === 'Escape') {
            setActive(null);
            setAnnouncement('');
          }
        }}
        onBlur={() => {
          setActive(null);
          setAnnouncement('');
        }}
      >
        <svg
          className={styles.chartSvg}
          height={HEIGHT}
          aria-hidden="true"
          viewBox={`0 0 ${String(width)} ${String(HEIGHT)}`}
          width={width}
          onPointerMove={(event) => {
            pick(event.clientX, event.currentTarget);
          }}
          onPointerLeave={() => {
            setActive(null);
          }}
        >
          {chart.yTicks.map((tick) => (
            <g key={tick.value}>
              <line className={styles.gridLine} x1={LEFT} x2={width - right} y1={y(tick.value)} y2={y(tick.value)} />
              <text className={styles.axisLabel} textAnchor="end" x={LEFT - 10} y={y(tick.value) + 3.5}>
                {tick.label}
              </text>
            </g>
          ))}

          {shown === null || active === null ? null : (
            <rect
              className={styles.columnHover}
              height={base - TOP + 8}
              rx={6}
              width={columnWidth}
              x={LEFT + columnWidth * active}
              y={TOP - 8}
            />
          )}

          {chart.steps.map((step) => {
            const level = y(step.value);
            const isLast = step.to === chart.columns.length - 1;
            return (
              <line
                className={styles.targetLine}
                key={`${String(step.from)}-${String(step.value)}`}
                x1={LEFT + columnWidth * step.from}
                x2={isLast ? width - right + 4 : LEFT + columnWidth * (step.to + 1)}
                y1={level}
                y2={level}
              />
            );
          })}

          {chart.columns.map((column, index) => (
            <Column
              base={base}
              barWidth={barWidth}
              column={column}
              cx={centre(index)}
              height={HEIGHT}
              key={column.key}
              y={y}
            />
          ))}

          {chart.lastTarget === null ? null : (
            <g>
              <rect
                className={styles.targetChip}
                height={20}
                rx={6}
                width={CHIP_WIDTH}
                x={width - right + 8}
                y={y(chart.lastTarget.value) - 10}
              />
              <text
                className={styles.targetChipText}
                textAnchor="middle"
                x={width - right + 8 + CHIP_WIDTH / 2}
                y={y(chart.lastTarget.value) + 4}
              >
                {chart.lastTarget.label}
              </text>
            </g>
          )}

          {weight === null ? null : (
            <g>
              {weight.ticks.map((tick) => (
                <text
                  className={styles.weightAxis}
                  key={tick.kg}
                  textAnchor="end"
                  x={width - 2}
                  y={weightY(tick.kg) + 3.5}
                >
                  {tick.label}
                </text>
              ))}
              <path
                className={styles.weightLine}
                d={weight.points
                  .map((point, order) => `${order === 0 ? 'M' : 'L'}${centre(point.index).toFixed(1)} ${weightY(point.kg).toFixed(1)}`)
                  .join(' ')}
              />
              {(() => {
                const last = weight.points[weight.points.length - 1];
                if (last === undefined) return null;
                // Clear of its own column's bar; the bars, their value labels, the dashed target
                // line and the target chip are all ink the label may not sit on.
                const x = centre(last.index) + Math.max(8, barWidth / 2 + 4);
                const taken = chart.columns.flatMap((column, index): LabelBox[] => {
                  if (column.value === null || column.kind === 'none' || column.kind === 'na') return [];
                  const top = y(column.value);
                  const bar = {
                    left: centre(index) - barWidth / 2,
                    right: centre(index) + barWidth / 2,
                    top,
                    bottom: base,
                  };
                  return column.valueLabel === null
                    ? [bar]
                    : [bar, labelBox(column.valueLabel, centre(index), top - 7, 'middle')];
                });
                if (chart.lastTarget !== null) {
                  const chipY = y(chart.lastTarget.value);
                  taken.push({
                    left: width - right + 8,
                    right: width - right + 8 + CHIP_WIDTH,
                    top: chipY - 10,
                    bottom: chipY + 10,
                  });
                }
                for (const step of chart.steps) {
                  const level = y(step.value);
                  const isLast = step.to === chart.columns.length - 1;
                  taken.push({
                    left: LEFT + columnWidth * step.from,
                    right: isLast ? width - right + 4 : LEFT + columnWidth * (step.to + 1),
                    top: level - 2,
                    bottom: level + 2,
                  });
                }
                return (
                  <>
                    <circle className={styles.weightDot} cx={centre(last.index)} cy={weightY(last.kg)} r={3} />
                    <text
                      className={styles.weightLast}
                      x={x}
                      y={weightLabelBaseline(weight.lastLabel, x, weightY(last.kg), taken)}
                    >
                      {weight.lastLabel}
                    </text>
                  </>
                );
              })()}
            </g>
          )}
        </svg>

        {shown === null || active === null ? null : (
          <div
            aria-hidden="true"
            className={`${String(styles.readout)} ${active > chart.columns.length / 2 ? String(styles.readoutLeft) : ''}`}
            style={{
              insetInlineStart:
                active > chart.columns.length / 2
                  ? undefined
                  : `${String(Math.round(LEFT + columnWidth * (active + 1)))}px`,
              insetInlineEnd:
                active > chart.columns.length / 2
                  ? `${String(Math.round(width - (LEFT + columnWidth * active)))}px`
                  : undefined,
            }}
          >
            <b className={styles.readoutTitle}>{shown.readout.title}</b>
            {shown.readout.lines.map((line) => (
              <span className={styles.readoutLine} key={line}>
                {line}
              </span>
            ))}
          </div>
        )}
      </div>

      <span className={hidden.srOnly} role="status" aria-live="polite">
        {announcement}
      </span>

      <table className={hidden.srOnly}>
        <caption>{chart.name}</caption>
        <thead>
          <tr>
            <th scope="col">{tableHeads.when}</th>
            <th scope="col">{tableHeads.reading}</th>
          </tr>
        </thead>
        <tbody>
          {chart.columns.map((column) => (
            <tr key={column.key}>
              <th scope="row">{column.readout.title}</th>
              <td>{column.readout.lines.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <ul className={styles.legend}>
        {legend.map((item) => (
          <li className={styles.legendItem} key={item.key}>
            <i aria-hidden="true" className={`${String(styles.swatch)} ${String(SWATCH_CLASS[item.key] ?? '')}`} />
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A small bold SVG label's footprint, estimated from its length (`--fs-eyebrow`, 10.5px bold). */
const LABEL_HEIGHT = 11;
const LABEL_CHAR = 6.6;

interface LabelBox {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

function labelBox(text: string, x: number, baseline: number, anchor: 'start' | 'middle'): LabelBox {
  const width = text.length * LABEL_CHAR;
  const left = anchor === 'middle' ? x - width / 2 : x;
  return { left: left - 2, right: left + width + 2, top: baseline - LABEL_HEIGHT - 2, bottom: baseline + 3 };
}

const overlaps = (a: LabelBox, b: LabelBox) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/** Above the point, else below it, else clear of whichever column value label it would sit on. */
function weightLabelBaseline(text: string, x: number, pointY: number, taken: readonly LabelBox[]): number {
  const candidates = [
    pointY - 6,
    pointY + LABEL_HEIGHT + 4,
    ...taken.flatMap((box) => [box.top - 4, box.bottom + LABEL_HEIGHT + 3]),
  ];
  const fits = (baseline: number) =>
    baseline - LABEL_HEIGHT >= 2 &&
    baseline <= HEIGHT - BOTTOM &&
    !taken.some((box) => overlaps(box, labelBox(text, x, baseline, 'start')));
  return candidates.find(fits) ?? pointY - 6;
}

const SWATCH_CLASS: Readonly<Record<string, string | undefined>> = {
  onTarget: styles.swatchOn,
  offTarget: styles.swatchOff,
  targetBand: styles.swatchTarget,
  targetFloor: styles.swatchTarget,
  plain: styles.swatchPlain,
  unjudged: styles.swatchPlain,
  noTarget: styles.swatchPlain,
  current: styles.swatchCurrent,
  sugar: styles.swatchSugar,
  average: styles.swatchNone,
  weight: styles.swatchWeight,
  weightWeekly: styles.swatchWeight,
};

function Column({
  column,
  cx,
  barWidth,
  base,
  height,
  y,
}: {
  readonly column: NutritionChartColumn;
  readonly cx: number;
  readonly barWidth: number;
  readonly base: number;
  readonly height: number;
  readonly y: (value: number) => number;
}) {
  const label =
    column.label === null ? null : (
      <text
        className={column.current ? styles.xLabelNow : styles.xLabel}
        textAnchor="middle"
        x={cx}
        y={height - 6}
      >
        {column.label}
      </text>
    );
  if (column.kind === 'none') {
    return (
      <g>
        <circle className={styles.emptyMark} cx={cx} cy={base - 4} r={3.5} />
        {label}
      </g>
    );
  }
  if (column.kind === 'na' || column.value === null) {
    return (
      <g>
        <text className={styles.missingMark} textAnchor="middle" x={cx} y={base - 2}>
          —
        </text>
        {label}
      </g>
    );
  }
  const top = y(column.value);
  const barHeight = Math.max(0, base - top);
  const sugarHeight = column.sugar === null ? 0 : Math.min(barHeight, base - y(column.sugar));
  const radius = Math.min(6, barWidth / 2);
  return (
    <g>
      <rect
        className={BAR_CLASS[column.kind]}
        height={barHeight}
        rx={radius}
        width={barWidth}
        x={cx - barWidth / 2}
        y={top}
      />
      {sugarHeight > 1 ? (
        <rect
          className={styles.sugar}
          height={sugarHeight}
          rx={Math.min(radius, sugarHeight / 2)}
          width={barWidth}
          x={cx - barWidth / 2}
          y={base - sugarHeight}
        />
      ) : null}
      {column.valueLabel === null ? null : (
        <text
          className={column.kind === 'warn' ? styles.valueLabelOff : styles.valueLabel}
          textAnchor="middle"
          x={cx}
          y={top - 7}
        >
          {column.valueLabel}
        </text>
      )}
      {label}
    </g>
  );
}
